import type { EventState, RoundState, ServerMessage } from '@bingo/shared';
import { syncServerTime } from './clock.js';

/**
 * Redutor do estado do evento.
 *
 * O servidor manda **estado completo** na conexão (`state:sync`) e incrementos
 * depois. Cada incremento carrega a rodada inteira junto, de propósito: assim
 * uma mensagem perdida não deixa o painel inconsistente — a próxima já o
 * corrige. O telão e o /controle usam o MESMO redutor, que é parte de nunca
 * divergirem.
 *
 * Mensagens que não alteram estado (a faixa de "bingo não confirmado", a
 * comemoração, o resumo) são tratadas como **efeitos** na tela, não aqui.
 */
export function reduceEventState(
  current: EventState | null,
  message: ServerMessage,
): EventState | null {
  if (message.type === 'state:sync') {
    syncServerTime(message.data.serverTime);
    return message.data;
  }

  if (!current) return current;

  const withRound = (round: RoundState): EventState => ({
    ...current,
    game: current.game ? { ...current.game, currentRound: round } : current.game,
  });

  switch (message.type) {
    case 'ball:drawn':
    case 'ball:undone':
    case 'round:updated':
    case 'winner:confirmed':
    case 'claim:result':
      return withRound(message.data.round);

    case 'round:started':
      return withRound(message.data.round);

    case 'claim:opened':
      return current.game?.currentRound
        ? withRound({
            ...current.game.currentRound,
            pendingClaim: {
              id: message.data.claimId,
              cardNumber: message.data.cardNumber,
              ballsAtClaim: message.data.ballsAtClaim,
              openedAt: new Date().toISOString(),
            },
          })
        : current;

    case 'auto:tick':
      return { ...current, autoDraw: message.data };

    case 'display:state':
      return { ...current, displays: message.data.displays };

    case 'settings:updated':
      return {
        ...current,
        settings: message.data.settings,
        event: { ...current.event, name: message.data.eventName },
      };

    case 'game:finished':
      return current.game
        ? {
            ...current,
            game: { ...current.game, endedAt: new Date().toISOString() },
            autoDraw: { running: false, intervalMs: current.autoDraw.intervalMs, nextAt: null },
          }
        : current;

    default:
      return current;
  }
}

/** Atalho usado por quase toda tela. */
export function currentRound(state: EventState | null): RoundState | null {
  return state?.game?.currentRound ?? null;
}
